<template>
  <!-- Create Preset Dialog -->
  <v-dialog
    v-model="createPresetDialog"
    max-width="400">
    <v-card
      v-if="createPresetDialog"
      class="dialog-card">
      <div class="pa-3">
        <div style="display: flex; align-items: center; gap: 8px">
          <v-icon color="primary">mdi-tune-variant</v-icon>
          <span style="font-weight: 600">Create Preset from Scene</span>
          <v-spacer />
          <v-btn
            icon
            variant="text"
            size="small"
            @click="createPresetDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
      <div class="px-5">
        <v-text-field
          v-model="presetName"
          label="Preset Name"
          autofocus
          filled
          @keyup.enter="confirmCreatePreset" />
      </div>
      <div class="modal-footer">
        <v-btn
          variant="flat"
          @click="createPresetDialog = false">
          Cancel
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          :disabled="!presetName.trim()"
          :loading="createPresetLoading"
          @click="confirmCreatePreset">
          Create Preset
        </v-btn>
      </div>
    </v-card>
  </v-dialog>

  <BaseEditModal
    :displayName="sceneMaster.name"
    helpFileName="EditSceneModal"
    :itemtype="'scene'"
    :tabs="tabs"
    :startTab="go_to_tab"
    :icon="'mdi-cube-outline'"
    :masterObj="sceneMaster"
    :saveFunc="saveScene"
    :deleteFunc="deleteScene"
    :instanceDeletable="true"
    @close="emit('close')"
    @locked="(bool) => emit('locked', bool)">
    <template v-slot:prepend-buttons>
      <v-tooltip
        location="top"
        text="Create Preset from Scene">
        <template v-slot:activator="{ props: tooltipProps }">
          <v-btn
            v-bind="tooltipProps"
            variant="flat"
            style="background-color: var(--inner-background)"
            class="mr-2"
            @click="openCreatePresetDialog()">
            <v-icon style="font-size: 22px">mdi-tune-variant</v-icon>
          </v-btn>
        </template>
      </v-tooltip>
      <v-tooltip
        location="top"
        text="Clone Scene">
        <template v-slot:activator="{ props: tooltipProps }">
          <v-btn
            v-bind="tooltipProps"
            variant="flat"
            style="background-color: var(--inner-background)"
            class="mr-2"
            @click="cloneScene()">
            <v-icon style="font-size: 22px">mdi-content-duplicate</v-icon>
          </v-btn>
        </template>
      </v-tooltip>
    </template>
  </BaseEditModal>
</template>
<script setup>
import { deluge, state } from '../../stores/deluge';
import { computed, ref } from 'vue';
import BaseEditModal from '../Shared/Modals/BaseEditModal.vue';
import { $http } from '../../stores/http';

import { unMergeSceneData } from '../../utils/nestedUnmerge';

// patch stuff
import { createPatch } from '../../utils/JSONpatch';

// Scene Sub Components
import SceneDetails from './SceneModalComponents/SceneDetails.vue';
import SceneVariables from './SceneModalComponents/SceneVariables.vue';
import ScenePlayConditions from './SceneModalComponents/ScenePlayConditions.vue';
import SceneTriggers from './SceneModalComponents/SceneTriggers.vue';

// template_index is actually a UID
const props = defineProps(['current_scene', 'current_channel', 'go_to_tab']);
const emit = defineEmits(['close', 'locked']);

const sceneMaster = JSON.parse(JSON.stringify(props.current_scene));

async function cloneScene() {
  try {
    const response = await $http(
      'POST',
      `/dynamic_engine/scene_from_scene?qid=${
        deluge.items[deluge.items[props.current_scene.did].pdid].id
      }&scene_id=${props.current_scene.uid}&update_deluge=true`
    );
  } catch (error) {
    console.log(error);
  }
  emit('close', true);
}

// Create Preset from Scene functionality
const createPresetDialog = ref(false);
const presetName = ref('');
const createPresetLoading = ref(false);

function openCreatePresetDialog() {
  // Default preset name to scene name
  presetName.value = sceneMaster.name || '';
  createPresetDialog.value = true;
}

async function confirmCreatePreset() {
  if (!presetName.value.trim()) return;

  createPresetLoading.value = true;
  try {
    // Get the template UID from the scene's scene_name property
    const templateUid = sceneMaster.scene_name;

    // Create the preset using the scene's svars as scene_data
    const presetData = {
      name: presetName.value.trim(),
      template_uid: templateUid,
      scene_data: sceneMaster.svars || {},
      tags: [],
    };

    await $http('POST', '/create_preset', JSON.stringify(presetData));
    state.notifications.push({ type: 'success', msg: `Preset "${presetData.name}" created` });
    setTimeout(() => state.notifications.shift(), 5000);
    createPresetDialog.value = false;
    presetName.value = '';
  } catch (error) {
    console.error('Failed to create preset:', error);
    state.notifications.push({ type: 'error', msg: 'Failed to create preset' });
    setTimeout(() => state.notifications.shift(), 5000);
  } finally {
    createPresetLoading.value = false;
  }
}

// isLive flag: true when this scene matches the channel's live_scene
const channelDid = computed(() => {
  try {
    return typeof props.current_channel === 'string'
      ? props.current_channel
      : props.current_channel?.uid || props.current_channel?.did || null;
  } catch (e) {
    return null;
  }
});

const isLive = computed(() => {
  try {
    const key = channelDid.value;
    if (!key) return false;
    const channel = deluge.channels?.[key];
    if (!channel) return false;
    const liveDid = channel.live_scene?.did;
    return liveDid === props.current_scene.uid;
  } catch (e) {
    return false;
  }
});

// Tabs - must be 0 indexed.
const tabs = [
  {
    id: 0,
    title: 'Details',
    icon: 'mdi-rename-box',
    display: true,
    component: SceneDetails,
  },
  {
    id: 1,
    title: 'Variables',
    icon: 'mdi-tune',
    display: true,
    component: SceneVariables,
    props: {
      isLive,
      channel_did: channelDid,
    },
  },
  {
    id: 2,
    title: 'Conditions',
    icon: 'mdi-motion-play-outline',
    display: true,
    component: ScenePlayConditions,
    props: {
      itemtype: 'scene',
    },
  },
  {
    id: 3,
    title: 'Triggers',
    icon: 'mdi-share-all',
    display: true,
    component: SceneTriggers,
    props: {
      current_channel: props.current_channel,
    },
  },
];

async function saveScene(sceneUID, newTemplateData) {
  const toSend = JSON.parse(JSON.stringify(newTemplateData));
  if ('editableComposedSD' in toSend) {
    const editedMergedObj = newTemplateData.editableComposedSD;
    delete toSend.editableComposedSD;
    const currentState = sceneMaster.svars;
    const newState = unMergeSceneData({
      context: 'scene',
      editedMergedObj: editedMergedObj,
      templates: deluge.templates,
      scene: sceneMaster,
    });

    const patch = createPatch(currentState, newState);

    toSend.patch_svars = patch;
  }
  return await $http(
    'PUT',
    '/dynamic_engine/update_scene',
    JSON.stringify(toSend)
  );
}

async function deleteScene() {
  return await $http(
    'DELETE',
    '/dynamic_engine/delete_scene?scene_uid=' + props.current_scene.uid
  );
}

// END Tabs
</script>

<style scoped></style>
